import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { LANGUAGES, t } from './i18n';

const LangContext = createContext();

const STORAGE_KEY = 'admin_lang';
const CODES = LANGUAGES.map(l => l.code);

/** The stored preference, or English when nothing valid is stored. */
function readStoredLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return CODES.includes(stored) ? stored : 'en';
  } catch (error) {
    // Private browsing or blocked storage: fall back rather than crash the
    // whole panel on a preference.
    return 'en';
  }
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  // Keep the document language in step so the browser's own UI (spellcheck,
  // font selection, screen readers) matches what is on screen.
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback(next => {
    if (!CODES.includes(next)) return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (error) {
      // The choice still applies for this session even if it cannot persist.
    }
    setLangState(next);
  }, []);

  /** Cycles EN → AZ → RU → EN. Kept for the compact header control. */
  const toggle = useCallback(() => {
    setLang(CODES[(CODES.indexOf(lang) + 1) % CODES.length]);
  }, [lang, setLang]);

  const tr = useCallback(key => t(lang, key), [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, toggle, tr, languages: LANGUAGES }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() { return useContext(LangContext); }
